import { withFaroRouterInstrumentation } from '@grafana/faro-react';
import { usePspOptions } from '@opengov/app-core/psp/context/psp.context';
import defer from 'lodash/defer';
import React, { useEffect, useMemo } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  IndexRouteObject,
  LazyRouteFunction,
  Outlet,
  Route,
  RouterProvider
} from 'react-router-dom';
import { LoadingPage } from '../../src/components/LoadingPage/LoadingPage';
import { store } from '../ReportingAppRenderer';
import { RouteErrorElement } from '../components/RouteErrorElement/RouteErrorElement';
import { Root } from './Root';
import { useCheckSession } from '../../src/hooks/useCheckSession';
import { rootLoader } from './rootLoader';

export const reportsRouteLazyCallback: LazyRouteFunction<IndexRouteObject> = () =>
  import(
    /* webpackChunkName: 'reportsPage.lazyRoute' */
    /* webpackPreload: true */
    '../../src/pages/reportsPage/reportsPage.lazyRoute'
  );

export const createRouter = ({ basename }: { basename: string }) =>
  withFaroRouterInstrumentation(
    createBrowserRouter(
      createRoutesFromElements(
        <Route path="/" errorElement={<RouteErrorElement store={store} />} element={<Root />}>
          <Route index loader={rootLoader(store)} />
          <Route path=":entitySubdomain">
            <Route index lazy={reportsRouteLazyCallback} />
            <Route path="folders/:encodedFolderPath" element={<Outlet />}>
              <Route index lazy={reportsRouteLazyCallback} />
            </Route>

            <Route
              path="report-builder"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyReportBuilderRoute' */
                  /* webpackPrefetch: true */
                  '../../src/pages/reportBuilderPage/reportBuilderPage.lazyRoute'
                )
              }
            />

            <Route
              path=":reportId"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyReportRoute' */
                  /* webpackPrefetch: true */
                  '../../application/reportExplorer/lazyReportRoute'
                )
              }
            />

            <Route
              path=":reportId/:reportConfigurationId"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyReportExplorerRoute' */
                  /* webpackPrefetch: true */
                  '../../application/reportExplorer/lazyReportExplorerRoute'
                )
              }
            />

            <Route
              path=":reportId/:reportConfigurationId/edit"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyReportEditRoute' */
                  /* webpackPrefetch: true */
                  '../../application/reportExplorer/lazyReportEditRoute'
                )
              }
            />

            <Route
              path=":reportId/settings"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyReportSettingsRoute' */
                  /* webpackPrefetch: true */
                  '../../application/reportSettings/lazyReportSettingsRoute'
                )
              }
            />

            <Route
              path=":reportId/chart-and-details/settings"
              lazy={() =>
                import(
                  /* webpackChunkName: 'chartAndDetailsLazyReportSettingsRoute' */
                  /* webpackPrefetch: true */
                  '../../application/reportSettings/lazyReportSettingsRoute'
                )
              }
            />

            <Route
              path="accountSummary/:reportId/:reportConfigurationId"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyAccountSummaryRedirectRoute' */
                  /* webpackPrefetch: true */
                  '../../application/reportExplorer/lazyAccountSummaryRedirectRoute'
                )
              }
            />

            <Route
              path="create"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyReportCreateRoute' */
                  /* webpackPrefetch: true */
                  '../../application/reportCreate/lazyReportCreateRoute'
                )
              }
            />

            <Route
              path="embedTest"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyEmbedTestRoute' */
                  /* webpackPrefetch: true */
                  '../../application/embedTest/lazyEmbedTestRoute'
                )
              }
            />

            <Route
              path=":reportId/views/:viewId/edit"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyReportSavedViewEditRoute' */
                  /* webpackPrefetch: true */
                  '../../application/reportSavedViewEdit/lazyReportSavedViewEditRoute'
                )
              }
            />

            <Route
              path="standard/:templateId/prompts"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyStandardReportPromptsRoute' */
                  /* webpackPrefetch: true */
                  '../../application/standardReportPrompts/lazyStandardReportPromptsRoute'
                )
              }
            />

            <Route
              path="standard/:templateId/settings"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyStandardReportSettingsRoute' */
                  /* webpackPrefetch: true */
                  '../../application/standardReportSettings/lazyStandardReportSettingsRoute'
                )
              }
            />

            <Route
              path="standard/:templateId"
              lazy={() =>
                import(
                  /* webpackChunkName: 'lazyStandardReportTemplateRoute' */
                  /* webpackPrefetch: true */
                  '../../application/standardReportExplorer/lazyStandardReportTemplateRoute'
                )
              }
            />
          </Route>
        </Route>
      ),
      { basename }
    )
  );

export function Router() {
  const pspOptions = usePspOptions();
  const { baseRouterPath: basename = '/reporting', activeEntity } = pspOptions ?? {};
  const entityFriendlyId = activeEntity?.friendlyId;
  const { isAuthenticated } = useCheckSession();

  const router = useMemo(() => {
    if (!isAuthenticated) {
      return null;
    }
    return createRouter({ basename });
  }, [basename, isAuthenticated]);

  useEffect(() => {
    if (router && entityFriendlyId) {
      defer(() => {
        // router.state.location.pathname is the raw browser pathname and includes
        // the basename prefix (e.g. '/reporting/mds/uuid'). React Router only strips
        // the basename inside RouterProvider via useLocation(). Strip it here so
        // the deep-link guard compares against the router-relative path ('/mds/uuid').
        const rawPath = router.state.location.pathname;
        const currentPath = rawPath.startsWith(basename) ? rawPath.slice(basename.length) || '/' : rawPath;
        // Match both the entity gallery root ('/mds') and any path within the
        // entity ('/mds/uuid', '/mds/uuid/config') to avoid a redundant
        // no-op navigation when refreshing at the gallery.
        const isAlreadyOnThisEntity =
          currentPath === `/${entityFriendlyId}` || currentPath.startsWith(`/${entityFriendlyId}/`);
        if (!isAlreadyOnThisEntity) {
          void router.navigate(`/${entityFriendlyId}`);
        }
      });
    }
  }, [router, entityFriendlyId, basename]);

  if (router) {
    return <RouterProvider router={router} />;
  }

  return <LoadingPage />;
}
